// SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
// Copyright (C) 2019-2020 (original work) Open Assessment Technologies SA ;
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License

import { router, routingTable } from '@/core';
import { locale } from '@/services/localeService';
import '@oat-sa-private/ui-identity/css/main.css';
import packageJson from '../package.json';
import { extractDomain, log } from './core/utils';
import { user } from '@/stores/auth.store.js';
import { configurationStore, currentTenantStore } from './stores/tenantConfiguration.store';
import { storeTokens, getTenant } from '@/services/authService';
import './locale/beMessages';
import './index.css';

window.__VERSION = packageJson.version;

async function startApplication() {
    locale.setDictionaryLoader();

    try {
        const domain = extractDomain(window.location.href);
        if (!domain) {
            const message = `Missing domain in window.location: ${window.location?.href ?? 'unknown'}`;
            log.warn(message);
            throw new Error(message);
        }

        try {
            await configurationStore.updateConfigurationData();
        } catch (error) {
            log.warn(`Failed to update configuration for domain ${domain}: ${error.message}`);
            throw error;
        }
        const configuration = configurationStore.get();
        const tenantId = configuration?.tenantId;

        if (!tenantId) {
            throw new Error(`Missing tenantId in configuration for domain: ${domain}`);
        }

        let tenantData = { tenantId, domain };
        try {
            const authTenantData = await getTenant();
            tenantData = { ...tenantData, ...authTenantData };
        } catch (error) {
            log.warn(`Failed to fetch tenant data from Auth Server: ${error.message}`);
        }
        currentTenantStore.set(tenantData);
    } catch (err) {
        log.warn(err.message);
        return;
    }

    if (window.SESSION_OVERRIDE) {
        await storeTokens(window.SESSION_OVERRIDE);
        log.warn('Session overridden', window.SESSION_OVERRIDE);
    }

    try {
        await user.updateInfo(); // throws error if token does not exist
        let { locale: defaultLocale } = configurationStore.get() || {};
        const { language: userLanguage } = user.get() || {};

        await locale.updateApplicationLocale(userLanguage || defaultLocale || '');
        if (user.get()?.userId) {
            await configurationStore.updateTenantRoles();
        }
    } catch (err) {
        log.warn(err.message);
    } finally {
        await router.start(routingTable, window.SESSION_OVERRIDE?.url);
    }
}

startApplication();
